<template>
  <div v-if="!store.isFrontendOnly" class="modal sp-modal-card" :class="{ 'is-active': store.showPreferencesModal }">
    <div class="modal-background" @click="store.closePreferencesModal"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Preferences</p>
        <button class="delete" aria-label="close" @click="store.closePreferencesModal"></button>
      </header>
      <section class="modal-card-body">
        <!-- Notifications Section -->
        <div class="mb-4">
          <h5 class="title is-6 mb-3">
            <span class="icon-text">
              <span class="icon">
                <FontAwesomeIcon :icon="faBell" />
              </span>
              <span>Notifications</span>
            </span>
          </h5>

          <div v-if="!notificationSupported" class="notification is-warning is-light">
            <p>Notifications are not supported in this browser.</p>
          </div>

          <div v-else class="ml-2 field">
            <label class="checkbox">
              <input
                type="checkbox"
                v-model="notificationsEnabled"
                @change="toggleNotifications"
                :disabled="isToggling"
              >
              Enable notifications
              <span v-if="isToggling" class="icon is-small ml-2">
                <FontAwesomeIcon :icon="faSpinner" spin />
              </span>
            </label>

            <div v-if="showPermissionWarning" class="has-text-danger is-size-7 mt-2">
              <FontAwesomeIcon :icon="faExclamationTriangle" class="mr-1" />
              Permission needed - click again to enable
            </div>

            <div v-if="notificationError" class="has-text-danger is-size-7 mt-2">
              <FontAwesomeIcon :icon="faExclamationTriangle" class="mr-1" />
              {{ notificationError }}
            </div>

            <p class="help mt-2">
              Get notified when timers start and end to stay focused on your work.
            </p>
          </div>
        </div>

        <!-- Time Zone Section -->
        <div class="mb-4">
          <h5 class="title is-6 mb-3">
            <span class="icon-text">
              <span class="icon">
                <FontAwesomeIcon :icon="faGlobe" />
              </span>
              <span>Time zone</span>
            </span>
          </h5>

          <div class="ml-2 field">
            <label class="label is-small" for="home-timezone-select">Home time zone</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select
                  id="home-timezone-select"
                  v-model="homeTimezone"
                  :disabled="isSavingTimezone"
                  @change="saveHomeTimezone"
                >
                  <option v-if="!homeTimezone" :value="''">Select a time zone…</option>
                  <option v-for="tz in zoneList" :key="tz" :value="tz">{{ tz }}</option>
                </select>
              </div>
            </div>
            <p class="help mt-1">
              Used to group your history by day. Defaults are interpreted as your home time zone, even when traveling.
              <button
                type="button"
                class="button is-small is-text p-0 ml-1"
                :disabled="isSavingTimezone || homeTimezone === deviceTimezone"
                @click="useDeviceTimezone"
              >
                Use device ({{ deviceTimezone }})
              </button>
            </p>
            <p v-if="timezoneError" class="help is-danger mt-1">{{ timezoneError }}</p>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot">
        <div class="buttons">
          <button class="button" @click="store.closePreferencesModal">Close</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBell, faSpinner, faExclamationTriangle, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useBlocksStore } from '../stores/blocks';
import { notificationService } from '../utils/notificationService.js';
import { getDeviceTimezone, listSupportedTimezones } from '../utils/time.js';

const store = useBlocksStore();
const isToggling = ref(false);
const showPermissionWarning = ref(false);
const notificationError = ref('');

const isSavingTimezone = ref(false);
const timezoneError = ref('');
const deviceTimezone = getDeviceTimezone();
const zoneList = listSupportedTimezones();

const notificationSupported = computed(() => notificationService.supported);

const notificationsEnabled = computed({
  get: () => store.getUserSetting('notifications_enabled', false),
  set: () => {} // Handled by toggleNotifications
});

const homeTimezone = ref(store.getUserSetting('home_timezone', '') || '');

const saveHomeTimezone = async () => {
  timezoneError.value = '';
  if (!homeTimezone.value) return;
  isSavingTimezone.value = true;
  try {
    await store.updateUserSettings({ home_timezone: homeTimezone.value });
  } catch (error) {
    console.error('Error saving time zone:', error);
    timezoneError.value = 'Failed to save time zone. Please try again.';
  } finally {
    isSavingTimezone.value = false;
  }
};

const useDeviceTimezone = async () => {
  homeTimezone.value = deviceTimezone;
  await saveHomeTimezone();
};

const toggleNotifications = async () => {
  if (isToggling.value) return;

  isToggling.value = true;
  showPermissionWarning.value = false;
  notificationError.value = '';

  try {
    const currentlyEnabled = store.getUserSetting('notifications_enabled', false);

    if (currentlyEnabled) {
      await store.disableNotifications();
    } else {
      const success = await store.enableNotifications();

      if (!success) {
        showPermissionWarning.value = true;
        setTimeout(() => {
          showPermissionWarning.value = false;
        }, 5000);
      }
    }
  } catch (error) {
    console.error('Error toggling notifications:', error);
    notificationError.value = 'Failed to update notification settings. Please try again.';
    setTimeout(() => {
      notificationError.value = '';
    }, 5000);
  } finally {
    isToggling.value = false;
  }
};
</script>
